import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
  PaperAirplaneIcon,
  EyeIcon,
  UserGroupIcon,
  InformationCircleIcon,
  XMarkIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/MasterAdmin/push-notifications/StatusBadge';
import SendConfirmModal from '../../components/MasterAdmin/push-notifications/SendConfirmModal';
import PushPanelShell from '../../components/MasterAdmin/push-notifications/PushPanelShell';
import { PUSH_HUB_BASE_PATH } from '../../models/MasterAdmin/pushNotificationHub';
import type { PushCampaignEditorLocationState } from '../../components/MasterAdmin/push-notifications/PushCampaignEditorPage';
import type { CampaignStatus, PushNotificationCampaign } from '../../models/MasterAdmin/pushNotificationCampaign';
import {
  deleteCampaign,
  fetchCampaigns,
  getCampaign,
  pauseCampaign,
  resumeCampaign,
  cancelCampaign,
} from '../../services/MasterAdmin/pushNotificationCampaignService';
import api from '../../utils/api';

const typeLabel = (t: string) =>
  ({
    OFFER: 'Offer',
    DISCOUNT: 'Discount',
    SERVICE: 'Service',
    REMINDER: 'Reminder',
    AI: 'AI',
    BLOG: 'Blog',
    ALERT: 'Alert',
    AWARENESS: 'Awareness',
  } as Record<string, string>)[t] || t;

const audienceLabel = (a: string) =>
  ({
    ALL_USERS: 'Registered & logged in',
    VENDORS: 'Vendors',
    DELIVERY_PARTNERS: 'Delivery Partners',
    DOCTORS: 'Doctors',
    AMBULANCE: 'Ambulance',
    INACTIVE_VENDORS: 'Inactive Vendors',
    LOGGED_OUT_VENDORS: 'Vendors (Logged-out)',
    PENDING_APPROVAL_VENDORS: 'Pending Approval Vendors',
    INACTIVE_DOCTORS: 'Inactive Doctors',
    PENDING_APPROVAL_DOCTORS: 'Pending Approval Doctors',
    LOGGED_OUT_DOCTORS: 'Doctors (Logged-out)',
    INACTIVE_AMBULANCE: 'Inactive Ambulance',
    PENDING_APPROVAL_AMBULANCE: 'Pending Approval Ambulance',
    LOGGED_OUT_AMBULANCE: 'Ambulance (Logged-out)',
    UNREGISTERED_GUESTS: 'Unregistered Guests',
    SINGLE_USER: 'Single User',
    SINGLE_DOCTOR: 'Single Doctor',
    SINGLE_AMBULANCE: 'Single Ambulance',
    SINGLE_VENDOR: 'Single Vendor',
    INACTIVE_USERS: 'Registered but Inactive',
    USERS_WITHOUT_HEALTH_RECORD: 'Users without Health Records',
    LOGGED_OUT_USERS: 'Logged out after registration',
  } as Record<string, string>)[a] || a;

function formatScheduleCell(row: PushNotificationCampaign): string {
  const type = String(row.scheduleType || (row.scheduledAt ? 'ONCE' : 'NOW')).toUpperCase();
  const labels: Record<string, string> = {
    NOW: 'Now',
    ONCE: 'Once',
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    EVERY_15_DAYS: 'Every 15 days',
    MONTHLY: 'Monthly',
  };
  return labels[type] || type;
}

function formatRunAt(iso: string | null | undefined, timeZone?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    timeZone: timeZone || 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

const SkeletonRows: React.FC = () => (
  <>
    {Array.from({ length: 6 }).map((_, i) => (
      <tr key={i} className="animate-pulse border-b border-gray-100 dark:border-gray-700">
        <td className="px-4 py-3"><div className="h-4 w-40 rounded bg-gray-200 dark:bg-gray-600" /></td>
        <td className="px-4 py-3"><div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-600" /></td>
        <td className="px-4 py-3"><div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-600" /></td>
        <td className="px-4 py-3"><div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-600" /></td>
        <td className="px-4 py-3"><div className="h-4 w-28 rounded bg-gray-200 dark:bg-gray-600" /></td>
        <td className="px-4 py-3"><div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-600" /></td>
        <td className="px-4 py-3"><div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-600" /></td>
        <td className="px-4 py-3"><div className="h-4 w-28 rounded bg-gray-200 dark:bg-gray-600" /></td>
        <td className="px-4 py-3"><div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-600" /></td>
        <td className="px-4 py-3"><div className="h-4 w-8 rounded bg-gray-200 dark:bg-gray-600" /></td>
      </tr>
    ))}
  </>
);

// ─── Info Tooltip ─────────────────────────────────────────────────────────────
const InfoTooltip: React.FC<{ text: string; colorClass?: string }> = ({
  text,
  colorClass = 'text-gray-400',
}) => {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  return (
    <div className="relative inline-flex">
      <button
        ref={ref}
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        className={`flex items-center ${colorClass} hover:opacity-80 transition-opacity`}
        aria-label="More information"
      >
        <InformationCircleIcon className="h-4 w-4" />
      </button>
      {show && (
        <div className="absolute top-full right-0 z-50 mt-2 w-64 rounded-xl border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-700 shadow-xl dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
          {text}
          <div className="absolute bottom-full right-2.5 h-2 w-2 translate-y-1 rotate-45 border-t border-l border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800" />
        </div>
      )}
    </div>
  );
};

// ─── Unregistered Users Detail Modal ─────────────────────────────────────────
interface UnregisteredUserRecord {
  index: number;
  deviceId: string;
  fcmTokenMasked: string;
  lastSeenAt: string | null;
  createdAt: string | null;
  source: string;
}

interface AudienceUserRecord {
  index: number;
  userId?: string;
  name?: string;
  contactNumber?: string;
  emailId?: string;
  registeredAt?: string | null;
  deviceId?: string;
  fcmTokenMasked?: string;
  lastSeenAt?: string | null;
  createdAt?: string | null;
  source?: string;
}

const AudienceDetailsModal: React.FC<{
  open: boolean;
  audienceType: 'unregistered' | 'loggedIn' | 'inactive' | 'loggedOut' | 'noHealthRecords' | '';
  onClose: () => void;
  onSendNotification: () => void;
}> = ({ open, audienceType, onClose, onSendNotification }) => {
  const [records, setRecords] = useState<AudienceUserRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !audienceType) return;
    setLoading(true);
    const url = audienceType === 'unregistered' 
      ? '/master-admin/campaigns/unregistered-users' 
      : `/master-admin/campaigns/audience-details?audience=${audienceType}`;
    
    api
      .get(url)
      .then((r) => {
        if (r.data?.success) setRecords(r.data.data ?? []);
      })
      .catch(() => toast.error('Could not load user details.'))
      .finally(() => setLoading(false));
  }, [open, audienceType]);

  if (!open || !audienceType) return null;

  const fmt = (d: string | null | undefined) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString();
    } catch {
      return d;
    }
  };

  const audienceConfig = {
    unregistered: {
      title: 'Unregistered Guest Devices',
      subtitle: 'These users installed the Vedika app but have not created an account yet. FCM tokens are masked for security.',
      emptyText: 'No unregistered guests found',
    },
    loggedIn: {
      title: 'Registered & Logged In Users',
      subtitle: 'Registered users with an active login session. Campaigns use their active user FCM token.',
      emptyText: 'No logged-in users found',
    },
    inactive: {
      title: 'Registered but Inactive Users',
      subtitle: 'These are registered users who haven\'t had any event activity recently.',
      emptyText: 'No inactive users found',
    },
    loggedOut: {
      title: 'Logged Out After Registration',
      subtitle: 'These are registered users who have logged out of the app.',
      emptyText: 'No logged out registered users found',
    },
    noHealthRecords: {
      title: 'Users without Health Records',
      subtitle: 'These are registered users (logged-in and logged-out) who have not uploaded any health records.',
      emptyText: 'All registered users have health records!',
    },
  }[audienceType];

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
              <DevicePhoneMobileIcon className="h-5 w-5 text-blue-600" />
              {audienceConfig?.title}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {audienceConfig?.subtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="space-y-3 p-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse flex items-center gap-4 rounded-xl border border-gray-100 p-3 dark:border-gray-700"
                >
                  <div className="h-8 w-8 shrink-0 rounded-full bg-gray-200 dark:bg-gray-600" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-48 rounded bg-gray-200 dark:bg-gray-600" />
                    <div className="h-3 w-32 rounded bg-gray-200 dark:bg-gray-600" />
                  </div>
                  <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-600" />
                </div>
              ))}
            </div>
          ) : records.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <UserGroupIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
              <p className="mt-3 text-sm font-medium text-gray-500 dark:text-gray-400">{audienceConfig?.emptyText}</p>
            </div>
          ) : (
            <div className="p-4">
              <p className="mb-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                {records.length} user{records.length !== 1 ? 's' : ''} found
              </p>
              <div className="space-y-2">
                {records.map((rec) => (
                  <div
                    key={rec.userId || rec.deviceId}
                    className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-gray-900/40"
                  >
                    {/* Index badge */}
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600/10 text-xs font-bold text-blue-600 dark:bg-blue-400/10 dark:text-blue-400">
                      {rec.index}
                    </div>

                    <div className="min-w-0 flex-1">
                      {audienceType === 'unregistered' ? (
                        <>
                          <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">Device ID</span>
                          <p className="mt-0.5 break-all font-mono text-xs text-gray-600 dark:text-gray-300">
                            {rec.deviceId}
                          </p>
                          <div className="mt-1.5 flex items-center gap-1.5">
                            <span className="text-xs text-gray-500 dark:text-gray-400">FCM Token:</span>
                            <code className="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                              {rec.fcmTokenMasked}
                            </code>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-bold text-gray-900 dark:text-white">{rec.name}</p>
                          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
                            <span className="font-semibold">User ID:</span> <span className="font-mono">{rec.userId}</span>
                          </p>
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                            <span>Phone: {rec.contactNumber}</span>
                            {rec.emailId && rec.emailId !== '(no email)' && <span>Email: {rec.emailId}</span>}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Timestamps */}
                    <div className="shrink-0 text-right text-xs">
                      {audienceType === 'unregistered' ? (
                        <>
                          <p className="text-gray-500 dark:text-gray-400">Last seen</p>
                          <p className="font-medium text-gray-700 dark:text-gray-200">{fmt(rec.lastSeenAt)}</p>
                          <p className="mt-1 text-gray-400 dark:text-gray-500">First seen</p>
                          <p className="text-gray-500 dark:text-gray-400">{fmt(rec.createdAt)}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-gray-400 dark:text-gray-500">Registered</p>
                          <p className="font-medium text-gray-700 dark:text-gray-200">{fmt(rec.registeredAt)}</p>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between border-t border-gray-100 px-5 py-3 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {records.length > 0
              ? `${records.length} recipient${records.length !== 1 ? 's' : ''} targetable`
              : ''}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => {
                onClose();
                onSendNotification();
              }}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Send Notification to All
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const PushCampaignsPanel: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState<PushNotificationCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');
  const [audienceFilter, setAudienceFilter] = useState('');

  const [sendOpen, setSendOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState<PushNotificationCampaign | null>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [totalItems, setTotalItems] = useState(0);

  /** Row whose actions menu is open (portal + fixed position). */
  const [actionMenuRow, setActionMenuRow] = useState<PushNotificationCampaign | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const [unregisteredCount, setUnregisteredCount] = useState<number | null>(null);
  const [loggedInCount, setLoggedInCount] = useState<number | null>(null);
  const [loggedInSource, setLoggedInSource] = useState<string | null>(null);
  const [inactiveCount, setInactiveCount] = useState<number | null>(null);
  const [loggedOutCount, setLoggedOutCount] = useState<number | null>(null);
  const [noHealthRecordsCount, setNoHealthRecordsCount] = useState<number | null>(null);

  // Audience details modal
  const [audienceDetailsOpen, setAudienceDetailsOpen] = useState(false);
  const [activeAudienceType, setActiveAudienceType] = useState<'unregistered' | 'loggedIn' | 'inactive' | 'loggedOut' | 'noHealthRecords' | ''>('');

  const openDetails = (type: 'unregistered' | 'loggedIn' | 'inactive' | 'loggedOut' | 'noHealthRecords') => {
    setActiveAudienceType(type);
    setAudienceDetailsOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, unregisteredRes] = await Promise.all([
        fetchCampaigns({
          page,
          limit: 20,
          ...(search ? { search } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(audienceFilter ? { targetAudience: audienceFilter } : {}),
          ...(typeFilter ? { notificationType: typeFilter } : {}),
        }),
        api
          .get('/master-admin/campaigns/stats')
          .then((r) => r.data)
          .catch((err) => {
            console.error('❌ Server Confession (The Real Error):', err.response?.data || err.message);
            return null;
          }),
      ]);
      setItems(res.data.items);
      setTotalPages(res.data.pagination.totalPages);
      setTotalItems(res.data.pagination.totalItems || 0);

      if (unregisteredRes?.success && unregisteredRes.stats) {
        setUnregisteredCount(unregisteredRes.stats.unregistered?.count ?? 0);
        const loggedInStat = unregisteredRes.stats.loggedIn;
        setLoggedInCount(
          loggedInStat?.count ??
            (typeof loggedInStat === 'number' ? loggedInStat : 0)
        );
        setLoggedInSource(loggedInStat?.source ?? null);
        setInactiveCount(unregisteredRes.stats.inactive?.count ?? 0);
        setLoggedOutCount(unregisteredRes.stats.loggedOut?.count ?? 0);
        setNoHealthRecordsCount(unregisteredRes.stats.noHealthRecords?.count ?? 0);
      }
    } catch {
      toast.error('Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, audienceFilter, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const state = location.state as (PushCampaignEditorLocationState & {
      type?: string;
      userId?: string;
      userName?: string;
      readyToSendId?: string;
    }) | null;

    if (state?.readyToSendId) {
      void getCampaign(state.readyToSendId)
        .then((res) => {
          setSendTarget(res.data);
          setSendOpen(true);
        })
        .catch(() => toast.error('Could not open send dialog.'));
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (state?.type === 'CHURN_REMINDER') {
      navigate(`${PUSH_HUB_BASE_PATH}/campaigns/new`, {
        replace: true,
        state: {
          prefills: {
            title: `We miss you, ${state.userName}!`,
            message: `Hi ${state.userName}, we haven't seen you in a while. Explore the latest health updates on Vedika!`,
            notificationType: 'REMINDER',
            targetAudience: 'SINGLE_USER',
            targetUserId: state.userId,
            intentType: 'USER_HOME',
            targetPlatform: 'BOTH',
          },
        } satisfies PushCampaignEditorLocationState,
      });
    }
  }, [location, navigate]);

  const openCreate = () => {
    navigate(`${PUSH_HUB_BASE_PATH}/campaigns/new`);
  };

  const openEdit = (row: PushNotificationCampaign) => {
    navigate(`${PUSH_HUB_BASE_PATH}/campaigns/${row.id}/edit`);
  };

  const draftWithPrefills = (prefills: NonNullable<PushCampaignEditorLocationState['prefills']>) => {
    navigate(`${PUSH_HUB_BASE_PATH}/campaigns/new`, {
      state: { prefills } satisfies PushCampaignEditorLocationState,
    });
  };

  const onDelete = async (row: PushNotificationCampaign) => {
    const confirmMsg =
      row.status === 'SENT'
        ? `Delete sent campaign "${row.title}"? This removes the record only; notifications already delivered stay on devices.`
        : `Delete "${row.title}"?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await deleteCampaign(row.id);
      toast.success('Notification deleted.');
      load();
    } catch {
      toast.error('Delete failed.');
    }
  };

  const onPause = async (row: PushNotificationCampaign) => {
    try {
      await pauseCampaign(row.id);
      toast.success('Campaign paused.');
      load();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      toast.error(msg || 'Could not pause campaign.');
    }
  };

  const onResume = async (row: PushNotificationCampaign) => {
    try {
      await resumeCampaign(row.id);
      toast.success('Campaign resumed.');
      load();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      toast.error(msg || 'Could not resume campaign.');
    }
  };

  const onCancel = async (row: PushNotificationCampaign) => {
    if (!window.confirm(`Cancel "${row.title}"? This cannot be resumed.`)) return;
    try {
      await cancelCampaign(row.id);
      toast.success('Campaign cancelled.');
      load();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      toast.error(msg || 'Could not cancel campaign.');
    }
  };

  const openSend = (row: PushNotificationCampaign) => {
    setActionMenuRow(null);
    setSendTarget(row);
    setSendOpen(true);
  };

  const openView = (row: PushNotificationCampaign) => {
    navigate(`${PUSH_HUB_BASE_PATH}/campaigns/${row.id}`);
  };

  const openActionsMenu = (row: PushNotificationCampaign, anchor: DOMRect) => {
    const w = 192;
    const margin = 8;
    const approxMenuH = 140;
    let top = anchor.bottom + 4;
    if (top + approxMenuH > window.innerHeight - margin) {
      top = Math.max(margin, anchor.top - approxMenuH - 4);
    }
    const left = Math.min(anchor.right - w, window.innerWidth - w - margin);
    setActionMenuPos({ top, left: Math.max(margin, left) });
    setActionMenuRow((prev) => (prev?.id === row.id ? null : row));
  };

  const openSendToUnregistered = () => {
    draftWithPrefills({
      title: 'Complete your registration',
      message: 'Welcome to Vedika! Finish your profile to unlock 24/7 healthcare access.',
      notificationType: 'REMINDER',
      targetAudience: 'UNREGISTERED_GUESTS',
      intentType: 'PROFILE_REGISTRATION',
      targetPlatform: 'BOTH',
    });
  };

  const hasFilters = Boolean(search || statusFilter || audienceFilter || typeFilter);

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setAudienceFilter('');
    setTypeFilter('');
    setPage(1);
  };

  const audienceCards: {
    key: 'unregistered' | 'loggedIn' | 'inactive' | 'loggedOut' | 'noHealthRecords';
    label: string;
    count: number | null;
    hint: string;
    onDraft: () => void;
  }[] = [
    {
      key: 'unregistered',
      label: 'Unregistered guests',
      count: unregisteredCount,
      hint: 'Guest devices that never registered',
      onDraft: openSendToUnregistered,
    },
    {
      key: 'loggedIn',
      label: 'Registered & logged in',
      count: loggedInCount,
      hint: loggedInSource === 'fcm_fallback'
        ? 'Active user FCM tokens (session fallback)'
        : 'Users with active session or FCM token',
      onDraft: () =>
        draftWithPrefills({
          title: 'A quick update for you',
          message: 'Open Vedika for the latest health services and offers.',
          notificationType: 'SERVICE',
          targetAudience: 'ALL_USERS',
          intentType: 'USER_HOME',
          targetPlatform: 'BOTH',
        }),
    },
    {
      key: 'inactive',
      label: 'Inactive users',
      count: inactiveCount,
      hint: 'Registered users inactive for a while',
      onDraft: () =>
        draftWithPrefills({
          title: 'We miss you on Vedika',
          message: 'Come back and explore care, medicines, and health records.',
          notificationType: 'REMINDER',
          targetAudience: 'INACTIVE_USERS',
          intentType: 'USER_HOME',
          targetPlatform: 'BOTH',
        }),
    },
    {
      key: 'loggedOut',
      label: 'Logged out users',
      count: loggedOutCount,
      hint: 'Registered users without an active session',
      onDraft: () =>
        draftWithPrefills({
          title: 'Sign in to continue',
          message: 'Sign in to sync appointments, orders, and health records.',
          notificationType: 'REMINDER',
          targetAudience: 'LOGGED_OUT_USERS',
          intentType: 'USER_LOGIN',
          targetPlatform: 'BOTH',
        }),
    },
    {
      key: 'noHealthRecords',
      label: 'No health records',
      count: noHealthRecordsCount,
      hint: 'Users who have not uploaded health records',
      onDraft: () =>
        draftWithPrefills({
          title: 'Upload your first Health Record!',
          message: 'Keep prescriptions and reports organised in one secure place.',
          notificationType: 'SERVICE',
          targetAudience: 'USERS_WITHOUT_HEALTH_RECORD',
          intentType: 'HEALTH_RECORDS',
          targetPlatform: 'BOTH',
        }),
    },
  ];

  return (
    <PushPanelShell
      title="Campaigns"
      description="Create, schedule, and send push campaigns. Use audience counts below to draft for a segment."
      actions={
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Create campaign
        </button>
      }
    >
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {audienceCards.map((card) => (
          <div
            key={card.key}
            className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="flex items-start justify-between gap-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{card.label}</p>
              <InfoTooltip text={card.hint} />
            </div>
            <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
              {card.count !== null ? card.count.toLocaleString() : '—'}
            </p>
            <div className="mt-2 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => openDetails(card.key)}
                className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:underline dark:text-gray-300 dark:hover:text-white"
              >
                View
              </button>
              <button
                type="button"
                onClick={card.onDraft}
                className="rounded-md border border-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Draft
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative min-w-0 flex-1 max-w-md">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              placeholder="Search by title…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearch(searchInput.trim());
                  setPage(1);
                }
              }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setSearch(searchInput.trim());
                setPage(1);
              }}
              className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white dark:bg-gray-100 dark:text-gray-900"
            >
              Search
            </button>
            <select
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as CampaignStatus | '');
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="RUNNING">Running</option>
              <option value="PAUSED">Paused</option>
              <option value="SENT">Sent</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="FAILED">Failed</option>
            </select>
            <select
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All types</option>
              <option value="OFFER">Offer</option>
              <option value="DISCOUNT">Discount</option>
              <option value="SERVICE">Service</option>
              <option value="REMINDER">Reminder</option>
              <option value="AI">AI</option>
              <option value="BLOG">Blog</option>
              <option value="ALERT">Alert</option>
              <option value="AWARENESS">Awareness</option>
            </select>
            <select
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              value={audienceFilter}
              onChange={(e) => {
                setAudienceFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All audiences</option>
              <option value="ALL_USERS">Registered & logged in</option>
              <option value="VENDORS">Vendors</option>
              <option value="DELIVERY_PARTNERS">Delivery Partners</option>
              <option value="DOCTORS">Doctors</option>
              <option value="INACTIVE_DOCTORS">Inactive Doctors</option>
              <option value="PENDING_APPROVAL_DOCTORS">Pending Approval Doctors</option>
              <option value="LOGGED_OUT_DOCTORS">Doctors (Logged-out)</option>
              <option value="AMBULANCE">Ambulance</option>
              <option value="INACTIVE_AMBULANCE">Inactive Ambulance</option>
              <option value="PENDING_APPROVAL_AMBULANCE">Pending Approval Ambulance</option>
              <option value="LOGGED_OUT_AMBULANCE">Ambulance (Logged-out)</option>
              <option value="INACTIVE_VENDORS">Inactive Vendors</option>
              <option value="LOGGED_OUT_VENDORS">Vendors (Logged-out)</option>
              <option value="PENDING_APPROVAL_VENDORS">Pending Approval Vendors</option>
              <option value="UNREGISTERED_GUESTS">Unregistered Guests</option>
              <option value="SINGLE_USER">Single User</option>
              <option value="SINGLE_DOCTOR">Single Doctor</option>
              <option value="SINGLE_AMBULANCE">Single Ambulance</option>
              <option value="SINGLE_VENDOR">Single Vendor</option>
              <option value="INACTIVE_USERS">Registered but Inactive</option>
              <option value="USERS_WITHOUT_HEALTH_RECORD">Users without Health Records</option>
              <option value="LOGGED_OUT_USERS">Logged out after registration</option>
            </select>
            {hasFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300"
              >
                <XMarkIcon className="h-4 w-4" />
                Clear
              </button>
            ) : null}
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {totalItems.toLocaleString()} campaign{totalItems === 1 ? '' : 's'}
          {hasFilters ? ' matching filters' : ''}
        </p>
      </div>

      {/* ── Campaigns Table ── */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Campaign</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Audience</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Schedule</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Last run</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Next run</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Sent</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Failed</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <SkeletonRows />
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                    No notifications yet. Create one to get started.
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => void openView(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openView(row);
                      }
                    }}
                    className="cursor-pointer hover:bg-gray-50/80 dark:hover:bg-gray-700/40"
                  >
                    <td className="max-w-[220px] px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      <span className="line-clamp-2">{row.title}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {typeLabel(row.notificationType)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {row.targetAudience === 'SINGLE_USER'
                        ? `User: ${row.targetUserId?.substring(0, 8)}...`
                        : audienceLabel(row.targetAudience)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {formatScheduleCell(row)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {formatRunAt(row.lastRunAt || row.sentAt, row.scheduleTimezone)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {formatRunAt(row.nextRunAt, row.scheduleTimezone)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-emerald-600 dark:text-emerald-400">
                      {Number(row.totalFcmDelivered ?? 0).toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-red-600 dark:text-red-400">
                      {Number(row.totalFcmFailed ?? 0).toLocaleString()}
                    </td>
                    <td
                      className="whitespace-nowrap px-4 py-3 text-right text-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={actionMenuRow?.id === row.id}
                        onClick={(e) => openActionsMenu(row, e.currentTarget.getBoundingClientRect())}
                        className="inline-flex rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        title="Actions"
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-gray-200 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-gray-200 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {sendOpen && sendTarget && (
        <SendConfirmModal
          key={sendTarget.id}
          open
          campaign={sendTarget}
          onClose={() => {
            setSendOpen(false);
            setSendTarget(null);
          }}
          onSent={() => {
            toast.success(sendTarget.status === 'SENT' ? 'Notification sent again.' : 'Notification sent.');
            load();
          }}
        />
      )}

      {/* Audience details modal */}
      <AudienceDetailsModal
        open={audienceDetailsOpen}
        audienceType={activeAudienceType}
        onClose={() => {
          setAudienceDetailsOpen(false);
          setActiveAudienceType('');
        }}
        onSendNotification={() => {
          if (activeAudienceType === 'unregistered') {
            openSendToUnregistered();
          } else if (activeAudienceType === 'loggedIn') {
            draftWithPrefills({
              title: 'Health updates for you',
              message: 'See the latest on your Vedika health dashboard.',
              notificationType: 'SERVICE',
              targetAudience: 'ALL_USERS',
              intentType: 'USER_HOME',
              targetPlatform: 'BOTH',
            });
          } else if (activeAudienceType === 'inactive') {
            draftWithPrefills({
              title: 'We miss you!',
              message: "It's been a while! Check out your updated health insights in Vedika.",
              notificationType: 'REMINDER',
              targetAudience: 'INACTIVE_USERS',
              intentType: 'USER_HOME',
              targetPlatform: 'BOTH',
            });
          } else if (activeAudienceType === 'loggedOut') {
            draftWithPrefills({
              title: 'Access your health records',
              message: 'Log back into your account to securely manage your health dashboard.',
              notificationType: 'REMINDER',
              targetAudience: 'LOGGED_OUT_USERS',
              intentType: 'USER_LOGIN',
              targetPlatform: 'BOTH',
            });
          } else if (activeAudienceType === 'noHealthRecords') {
            draftWithPrefills({
              title: 'Upload your first Health Record!',
              message: 'Keep all your medical prescriptions and reports organized in one secure place.',
              notificationType: 'SERVICE',
              targetAudience: 'USERS_WITHOUT_HEALTH_RECORD',
              intentType: 'HEALTH_RECORDS',
              targetPlatform: 'BOTH',
            });
          }
        }}
      />

      {actionMenuRow &&
        createPortal(
          <>
            <button
              type="button"
              aria-label="Close menu"
              className="fixed inset-0 z-[60] cursor-default bg-transparent"
              onClick={() => setActionMenuRow(null)}
            />
            <div
              role="menu"
              className="fixed z-[70] w-48 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-600 dark:bg-gray-800"
              style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  const r = actionMenuRow;
                  setActionMenuRow(null);
                  void openView(r);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                <EyeIcon className="h-4 w-4 shrink-0 text-gray-600 dark:text-gray-300" />
                View
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  const r = actionMenuRow;
                  setActionMenuRow(null);
                  void openEdit(r);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                <PencilSquareIcon className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                Edit
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  const r = actionMenuRow;
                  setActionMenuRow(null);
                  void onDelete(r);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                <TrashIcon className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                Delete
              </button>
              {['SCHEDULED', 'RUNNING'].includes(actionMenuRow.status) && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    const r = actionMenuRow;
                    setActionMenuRow(null);
                    void onPause(r);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  Pause
                </button>
              )}
              {actionMenuRow.status === 'PAUSED' && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    const r = actionMenuRow;
                    setActionMenuRow(null);
                    void onResume(r);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  Resume
                </button>
              )}
              {['DRAFT', 'SCHEDULED', 'RUNNING', 'PAUSED', 'FAILED', 'SENDING'].includes(actionMenuRow.status) && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    const r = actionMenuRow;
                    setActionMenuRow(null);
                    void onCancel(r);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel campaign
                </button>
              )}
              {actionMenuRow.status === 'SENT' ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    const r = actionMenuRow;
                    setActionMenuRow(null);
                    openSend(r);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  <PaperAirplaneIcon className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  Send again
                </button>
              ) : actionMenuRow.status === 'SCHEDULED' ? (
                <span className="block px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                  Sends automatically at scheduled time
                </span>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    const r = actionMenuRow;
                    setActionMenuRow(null);
                    openSend(r);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  <PaperAirplaneIcon className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  Send
                </button>
              )}
            </div>
          </>,
          document.body
        )}
    </div>
    </PushPanelShell>
  );
};

export default PushCampaignsPanel;
